import { IntegrationCard } from './IntegrationCard';
import { IntegrationTile } from './integrationLogos';
import { useState } from 'react';
import { useIntegration, useConnectTrello, useDisconnectTrello } from '../../hooks/useIntegrations';
import { useIntegrationAvailability } from '../../hooks/useIntegrationAvailability';
import { log } from '@/lib/logger';
import type { IntegrationMetadata } from '@overlayqa/shared';

log.info('[TrelloIntegrationCard] Module loading...');

export function TrelloIntegrationCard() {
  const { integration, isConnected, isLoading, refetch } = useIntegration('trello');
  const { available, reason: unavailableReason } = useIntegrationAvailability('trello');
  const connectMutation = useConnectTrello();
  const disconnectMutation = useDisconnectTrello();

  const [actionError, setActionError] = useState<string | null>(null);

  const isConnecting = connectMutation.isPending;
  const isDisconnecting = disconnectMutation.isPending;

  const handleConnect = () => {
    // Never fire a connect the backend can't complete — it's a guaranteed 500.
    if (!available) return;
    setActionError(null);
    connectMutation.mutate(undefined, {
      onError: (err) => {
        setActionError(err.message || 'Failed to connect Trello. Try again.');
      },
    });
  };

  const handleDisconnect = () => {
    setActionError(null);
    disconnectMutation.mutate(undefined, {
      onError: (err) => {
        setActionError(err.message || 'Failed to disconnect Trello. Try again.');
      },
    });
  };

  return (
    <IntegrationCard
      provider="trello"
      loading={isLoading}
      tile={<IntegrationTile color="var(--color-trello)"><TrelloIcon /></IntegrationTile>}
      name="Trello"
      description="Push captured issues to a Trello board with screenshots and context."
      connection={{
        connected: isConnected,
        connecting: isConnecting,
        disconnecting: isDisconnecting,
        onConnect: handleConnect,
        onDisconnect: handleDisconnect,
        onRetry: () => void refetch(),
        available,
        unavailableReason,
        connectedAt: integration?.createdAt,
        rows: [['Account', trelloAccount(integration?.providerMetadata)]],
      }}
      errors={[actionError]}
    />
  );
}

/** "Priya Menon (@priyamenon)", or whichever half the connection stored. */
function trelloAccount(metadata?: IntegrationMetadata): string | undefined {
  if (!metadata?.username) return metadata?.fullName;
  return metadata.fullName ? `${metadata.fullName} (@${metadata.username})` : `@${metadata.username}`;
}

/**
 * Trello board icon — three vertical rectangles, mirroring Trello's column layout.
 */
function TrelloIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <rect x="2" y="3" width="6" height="14" rx="1.5" fill="white"/>
      <rect x="9" y="3" width="6" height="9" rx="1.5" fill="white"/>
      <rect x="16" y="3" width="6" height="11" rx="1.5" fill="white"/>
    </svg>
  );
}

log.info('[TrelloIntegrationCard] Module loaded');
